/**
 * Smart scene selection (spec section 26).
 *
 * Transparent weighted selection - every candidate keeps its score
 * breakdown so the UI can show *why* a scene was chosen instead of
 * pretending the choice was magic.
 *
 *   score = moodMatch * 5
 *         + tagMatch  * 3
 *         + weight    * 2
 *         - recentUsagePenalty
 *
 * recentUsagePenalty is itself the sum of two bounded terms:
 *   recencyPenalty : up to 3.0, full inside the recent window and
 *                    decaying linearly to 0 across `recencyWindowHours`
 *   usagePenalty   : up to 2.0, scaled against the most-used scene
 *
 * All randomness is injected, so a seeded RNG makes selection testable.
 */

import { MOODS } from '../core/config.js';
import { systemRng } from '../utils/rng.js';

export const SCORE_WEIGHTS = { mood: 5, tag: 3, weight: 2, recency: 3, usage: 2 };

export const RECENCY_WINDOW_HOURS = 24;

export function getMoodTags(moodId) {
  return MOODS.find((m) => m.id === moodId)?.tags ?? [];
}

/** Fraction of the mood's tags that the scene actually carries. */
export function tagMatchRatio(scene, moodId) {
  const moodTags = getMoodTags(moodId);
  if (!moodTags.length) return 0;
  const sceneTags = new Set((scene.tags ?? []).map((t) => String(t).toLowerCase()));
  const hits = moodTags.filter((t) => sceneTags.has(t)).length;
  return hits / moodTags.length;
}

export function scoreScene(scene, { mood = null, now = Date.now(), maxUsage = 1, recencyWindowHours = RECENCY_WINDOW_HOURS } = {}) {
  const moodMatch = mood && scene.moods?.includes(mood) ? 1 : 0;
  const tagMatch = tagMatchRatio(scene, mood);
  const weightNorm = Math.min(1, Math.max(0, (Number(scene.weight) || 0) / 10));

  let recencyPenalty = 0;
  if (scene.lastUsed) {
    const hours = (now - new Date(scene.lastUsed).getTime()) / 3_600_000;
    if (Number.isFinite(hours) && hours >= 0) {
      recencyPenalty = Math.max(0, 1 - hours / recencyWindowHours) * SCORE_WEIGHTS.recency;
    }
  }

  const usagePenalty =
    maxUsage > 0 ? Math.min(1, (Number(scene.usageCount) || 0) / maxUsage) * SCORE_WEIGHTS.usage : 0;

  const total =
    moodMatch * SCORE_WEIGHTS.mood +
    tagMatch * SCORE_WEIGHTS.tag +
    weightNorm * SCORE_WEIGHTS.weight -
    recencyPenalty -
    usagePenalty;

  return {
    sceneId: scene.id,
    total: round(total),
    breakdown: {
      moodMatch: round(moodMatch * SCORE_WEIGHTS.mood),
      tagMatch: round(tagMatch * SCORE_WEIGHTS.tag),
      weight: round(weightNorm * SCORE_WEIGHTS.weight),
      recencyPenalty: round(-recencyPenalty),
      usagePenalty: round(-usagePenalty),
    },
    signals: { moodMatch, tagMatch, weightNorm },
  };
}

/** Rank all candidate scenes. Pure - safe to call from a test. */
export function rankScenes(scenes, options = {}) {
  const pool = scenes.filter((s) => s.enabled !== false);
  const maxUsage = pool.reduce((max, s) => Math.max(max, Number(s.usageCount) || 0), 0) || 1;
  return pool
    .map((scene) => ({ scene, ...scoreScene(scene, { ...options, maxUsage }) }))
    .sort((a, b) => b.total - a.total || a.scene.id.localeCompare(b.scene.id));
}

/**
 * Roulette-wheel pick over shifted scores. Shifting so the weakest
 * candidate still has a non-zero chance keeps the library varied -
 * a strict argmax would always return the same scene.
 */
function weightedPick(ranked, rng, { lastSceneId = null } = {}) {
  const eligible = ranked.filter((entry) => entry.scene.id !== lastSceneId);
  const candidates = eligible.length ? eligible : ranked;
  if (!candidates.length) return null;
  const min = Math.min(...candidates.map((c) => c.total));
  const weights = candidates.map((c) => Math.max(0.05, c.total - min + 0.5));
  const sum = weights.reduce((a, b) => a + b, 0);
  let roll = rng() * sum;
  for (let i = 0; i < candidates.length; i += 1) {
    roll -= weights[i];
    if (roll <= 0) return candidates[i];
  }
  return candidates[candidates.length - 1];
}

/**
 * Choose a scene.
 *
 * @param {Array} scenes  scene definitions
 * @param {object} options
 * @param {'random'|'mood'|'auto'} options.mode
 * @param {string|null} options.mood
 * @param {Function} options.rng    seeded RNG (defaults to system RNG)
 * @param {string|null} options.lastSceneId  avoids an immediate repeat
 * @param {number} options.now
 * @returns {{scene: object|null, ranked: Array, mode: string, reason: string}}
 */
export function selectScene(scenes, options = {}) {
  const { mode = 'auto', mood = null, rng = systemRng, lastSceneId = null, now = Date.now() } = options;

  const pool = scenes.filter((s) => s.enabled !== false);
  if (!pool.length) {
    return { scene: null, ranked: [], mode, reason: 'no enabled scenes in the library' };
  }

  if (mode === 'random') {
    const ranked = rankScenes(pool, { mood: null, now });
    const picked = weightedPick(ranked, rng, { lastSceneId });
    return { scene: picked?.scene ?? null, ranked, mode, reason: 'uniform random across the enabled library' };
  }

  if (mode === 'mood' && mood) {
    const moodPool = pool.filter((s) => s.moods?.includes(mood));
    if (moodPool.length) {
      const ranked = rankScenes(moodPool, { mood, now });
      const picked = weightedPick(ranked, rng, { lastSceneId });
      return { scene: picked?.scene ?? null, ranked, mode, reason: `restricted to the ${mood} mood` };
    }
    // Nothing carries the mood: fall through to automatic scoring so the
    // user always gets a result instead of an empty screen.
  }

  const ranked = rankScenes(pool, { mood, now });
  const picked = weightedPick(ranked, rng, { lastSceneId });
  const reason = mood
    ? `weighted by mood match, tags, weight and recent usage (mood: ${mood})`
    : 'weighted by tags, weight and recent usage';
  return { scene: picked?.scene ?? null, ranked, mode: mode === 'mood' ? 'auto' : mode, reason };
}

function round(value) {
  const result = Math.round(value * 1000) / 1000;
  // Normalise negative zero: a zero penalty must compare equal to 0.
  return result === 0 ? 0 : result;
}
